import React from 'react';
import { createPortal } from 'react-dom';

interface Props {
    items: any[];
    bindName: string;
    onSelect: (selectedItem: any) => void;

    selected?: any;
    placeholder?: string;
    disabled?: boolean;
    className?: string;
    verticalPadding?: string;
    id?: string;
    isServiceCard?: boolean;
}

interface State {
    isExpanded: boolean;
    dropdownPosition: { top: number; left: number; width: number };
}

class NewDropdown extends React.Component<Props, State> {
    dropdownRef: React.RefObject<HTMLDivElement>;

    constructor(props: Props) {
        super(props);

        this.state = {
            isExpanded: false,
            dropdownPosition: { top: 0, left: 0, width: 0 },
        };

        this.dropdownRef = React.createRef();
        this.onClickOutside = this.onClickOutside.bind(this);
        this.updateDropdownPosition = this.updateDropdownPosition.bind(this);
    }

    togglePicker(open: boolean) {
        this.setState({ isExpanded: open }, () => {
            if (open) {
                this.updateDropdownPosition();
                document.addEventListener('click', this.onClickOutside);
            } else {
                document.removeEventListener('click', this.onClickOutside);
            }
        });
    }

    updateDropdownPosition() {
        const dropdownButton = this.dropdownRef.current;
        if (dropdownButton) {
            const rect = dropdownButton.getBoundingClientRect();
            this.setState({
                dropdownPosition: {
                    top: rect.bottom + window.scrollY,
                    left: rect.left + window.scrollX,
                    width: rect.width,
                },
            });
        }
    }

    onClickOutside(e: any) {
        const target = document.querySelector(`#id${this.props.id}`);
        const withinBoundaries = e.composedPath().includes(target);

        if (!withinBoundaries) {
            this.togglePicker(false);
        }
    }

    renderDropdown() {
        const { items, bindName, selected, onSelect, isServiceCard = false } = this.props;
        const { dropdownPosition } = this.state;

        return createPortal(
            <div
                id={'id' + this.props.id}
                style={{
                    position: 'absolute',
                    top: dropdownPosition.top,
                    left: dropdownPosition.left,
                    width: dropdownPosition.width,
                    zIndex: 9999, // Ensure the dropdown is on top of everything
                }}
                className={`bg-white rounded divide-y divide-gray-100 shadow border border-blue-500 ${
                    !isServiceCard && 'w-full'
                }`}
            >
                <ul className="py-1 text-sm text-gray-700">
                    {items.map((item) => (
                        <li
                            className={`block clickable ${
                                item.id === selected?.id ? 'bg-blue-400 text-white' : 'hover:bg-gray-100'
                            } py-2 px-4 font-semibold`}
                            key={item.id}
                            onClick={() => {
                                this.togglePicker(false);
                                onSelect(item);
                            }}
                        >
                            {item[bindName]}
                        </li>
                    ))}
                </ul>
            </div>,
            document.body, // Render the dropdown at the root of the DOM
        );
    }

    render() {
        const {
            selected,
            items,
            bindName,
            placeholder = 'Select',
            disabled = false,
            className = '',
            verticalPadding = '',
        } = this.props;
        const { isExpanded } = this.state;

        return (
            <div className="relative w-48" ref={this.dropdownRef}>
                <button
                    id="dropdownDefault"
                    className={`${
                        disabled && 'cursor-default'
                    } ${className} w-full border border-blue-500 bg-white text-blue-500 font-bold focus:ring-1 focus:outline-none focus:ring-blue-300 rounded-lg px-4 ${
                        verticalPadding || 'py-1'
                    } text-center inline-flex items-center`}
                    type="button"
                    onClick={() => this.togglePicker(!isExpanded)}
                    disabled={disabled}
                >
                    {!!selected && items.find((item) => item.id === selected?.id)[bindName]}
                    {!!!selected && placeholder}
                    {!!!disabled && (
                        <svg
                            className="ml-auto w-4 h-4"
                            aria-hidden="true"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                        </svg>
                    )}
                </button>

                {isExpanded && this.renderDropdown()}
            </div>
        );
    }
}

export default NewDropdown;
